/**
 * DEV-286 (spec 142) — critérios de potencial da avaliação do App de Gestores.
 *
 * Constante ÚNICA do App Consultor para chave → rótulo e ordem canônica
 * (AC-18: os mesmos rótulos e a mesma ordem do App Gestores). A API não traz
 * rótulo nenhum — só a chave; quem rotula é esta tabela. Mudou no App Gestores
 * → mude aqui (e o teste que pina os literais acusa).
 */
export const POTENTIAL_CRITERIA = [
  { key: 'ambicao', label: 'Ambição' },
  { key: 'julgamento', label: 'Julgamento' },
  { key: 'learning_agility', label: 'Learning Agility' },
  { key: 'resiliencia', label: 'Resiliência' },
  { key: 'empatia', label: 'Empatia' },
] as const;

export type PotentialCriterionKey = (typeof POTENTIAL_CRITERIA)[number]['key'];

/** Nota inteira 1..5 — o zero não existe (estrela nenhuma = não preenchido). */
export type PotentialCriterionValue = 1 | 2 | 3 | 4 | 5;

/** Par critério–valor como o BFF entrega (`potentialCriteria[]`). */
export interface PotentialCriterionInput {
  criterion: PotentialCriterionKey;
  value: PotentialCriterionValue;
}

/** Linha pronta para a tela: chave, rótulo e nota, na ordem canônica. */
export interface PotentialCriterionScore {
  key: PotentialCriterionKey;
  label: string;
  value: PotentialCriterionValue;
}

const CRITERION_KEYS = new Set<string>(POTENTIAL_CRITERIA.map((c) => c.key));

function isValidValue(value: unknown): value is PotentialCriterionValue {
  return Number.isInteger(value) && (value as number) >= 1 && (value as number) <= 5;
}

/**
 * Deriva as cinco linhas a partir do campo opcional do BFF — DEFENSIVO (AC-19):
 * só uma lista com EXATAMENTE as cinco chaves canônicas, cada uma uma vez e
 * com nota inteira 1..5, vira linhas. Qualquer outra forma (ausente, `null`,
 * não-lista, parcial, chave desconhecida ou repetida, 0, 6, fracionário,
 * string) devolve `null`: nenhum bloco, nenhuma estrela vazia, nunca um zero
 * inventado. A saída sai sempre na ordem canônica — o rótulo manda, não a
 * posição no payload.
 */
export function derivePotentialCriteria(raw: unknown): PotentialCriterionScore[] | null {
  if (!Array.isArray(raw) || raw.length !== POTENTIAL_CRITERIA.length) return null;
  const byKey = new Map<string, PotentialCriterionValue>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') return null;
    const { criterion, value } = item as { criterion?: unknown; value?: unknown };
    if (typeof criterion !== 'string' || !CRITERION_KEYS.has(criterion)) return null;
    if (byKey.has(criterion)) return null;
    if (!isValidValue(value)) return null;
    byKey.set(criterion, value);
  }
  return POTENTIAL_CRITERIA.map((c) => {
    const value = byKey.get(c.key);
    // Cinco itens, cinco chaves distintas e todas canônicas ⇒ nenhuma falta.
    return { key: c.key, label: c.label, value: value as PotentialCriterionValue };
  });
}
